import { useEffect, useRef, useCallback, useState } from 'react';
import { useAppStore } from '../store';
import type { WSMessage } from '../types';

type MessageHandler = (message: WSMessage) => void;

interface UseWebSocketOptions {
  url?: string;
  autoConnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  onMessage?: MessageHandler;
}

interface UseWebSocketReturn {
  isConnected: boolean;
  lastMessage: WSMessage | null;
  sendMessage: (message: Record<string, unknown>) => void;
  subscribe: (type: string, handler: MessageHandler) => () => void;
  connect: () => void;
  disconnect: () => void;
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    url = import.meta.env.VITE_WS_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`,
    autoConnect = true,
    reconnectInterval = 3000,
    maxReconnectAttempts = 10,
    onMessage,
  } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handlersRef = useRef<Map<string, Set<MessageHandler>>>(new Map());

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WSMessage | null>(null);

  const addNotification = useAppStore((s) => s.addNotification);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const token = localStorage.getItem('helios_token');
      const wsUrl = token ? `${url}?token=${token}` : url;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        reconnectCountRef.current = 0;
        console.log('[Helios WS] Connected');
      };

      ws.onmessage = (event) => {
        try {
          const message: WSMessage = JSON.parse(event.data);
          setLastMessage(message);

          // Call global handler
          onMessage?.(message);

          // Call type-specific handlers
          const typeHandlers = handlersRef.current.get(message.type);
          if (typeHandlers) {
            typeHandlers.forEach((handler) => handler(message));
          }

          // Auto-notification for alerts
          if (message.type === 'alert') {
            const payload = message.payload as { title?: string; severity?: string };
            addNotification({
              title: 'New Alert',
              message: payload.title || 'New alert received',
              type: payload.severity === 'critical' || payload.severity === 'high' ? 'error' : 'warning',
            });
          }
        } catch (err) {
          console.error('[Helios WS] Failed to parse message:', err);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        console.log('[Helios WS] Disconnected:', event.code, event.reason);

        if (reconnectCountRef.current < maxReconnectAttempts && !event.wasClean) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectCountRef.current++;
            console.log(`[Helios WS] Reconnecting (${reconnectCountRef.current}/${maxReconnectAttempts})...`);
            connect();
          }, reconnectInterval * Math.min(reconnectCountRef.current + 1, 5));
        }
      };

      ws.onerror = (error) => {
        console.error('[Helios WS] Error:', error);
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('[Helios WS] Failed to connect:', err);
    }
  }, [url, reconnectInterval, maxReconnectAttempts, onMessage, addNotification]);

  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectCountRef.current = maxReconnectAttempts; // Prevent auto-reconnect
    wsRef.current?.close(1000, 'Client disconnect');
    wsRef.current = null;
  }, [maxReconnectAttempts]);

  const sendMessage = useCallback((message: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('[Helios WS] Cannot send - not connected');
    }
  }, []);

  const subscribe = useCallback((type: string, handler: MessageHandler): (() => void) => {
    if (!handlersRef.current.has(type)) {
      handlersRef.current.set(type, new Set());
    }
    handlersRef.current.get(type)!.add(handler);

    return () => {
      handlersRef.current.get(type)?.delete(handler);
    };
  }, []);

  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return { isConnected, lastMessage, sendMessage, subscribe, connect, disconnect };
}
